// A. Corporate identity + B. Launch readiness detail.
//
// The screen enforces the same rule the backend does: "Verified" is not a
// status anyone can select. It is only reachable through the Verify action,
// which asks for the evidence and the reason, and the form says so.
import { useCallback, useEffect, useState } from "react";
import { Eye, EyeOff, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import {
  CAP, LAUNCH_STATE_LABELS, can, fmtDate, humanise, launchTone, legalError,
  plGet, plPatch, plPost, statusLabel,
} from "../../lib/platformLegal";
import {
  Button, Empty, ExternalActionNote, Field, Loading, Modal, Note, Section, StatusPill, Table,
  TonePill, inputClass,
} from "./ui";

// Statuses a person may record directly. "verified" is deliberately absent.
const SELECTABLE = ["not_configured", "action_required", "in_progress", "pending_external",
                    "recorded_not_verified", "review_required", "not_applicable"];

export default function IdentitySection({ capabilities }) {
  const [identity, setIdentity] = useState(null);
  const [launch, setLaunch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reveal, setReveal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [verifying, setVerifying] = useState(null);
  const writable = can(capabilities, CAP.identity);

  const load = useCallback(async (withReveal = reveal) => {
    setLoading(true);
    try {
      const [snapshot, readiness] = await Promise.all([
        plGet("/identity", { params: withReveal ? { reveal: true } : {} }),
        plGet("/launch"),
      ]);
      setIdentity(snapshot);
      setLaunch(readiness);
    } catch (err) {
      toast.error(legalError(err));
    } finally {
      setLoading(false);
    }
  }, [reveal]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Loading label="Opening the corporate identity record..." />;
  if (!identity) return <Empty>The corporate identity record could not be loaded.</Empty>;

  const toggleReveal = async () => {
    const next = !reveal;
    setReveal(next);
    await load(next);
    if (next) toast.info("Revealing sensitive identifiers is recorded in the audit trail.");
  };

  return (
    <div className="space-y-4">
      <Section testId="pl-identity"
               title="Corporate identity"
               description="The one canonical answer to who legally operates ConfirmPH. Every public
                            surface reads from here, so nothing can be claimed that is not recorded."
               actions={writable && (
                 <Button variant="secondary" onClick={toggleReveal} data-testid="pl-reveal">
                   {reveal ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                   {reveal ? "Hide identifiers" : "Reveal identifiers"}
                 </Button>
               )}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
          {identity.fields.map((field) => (
            <FieldCard key={field.key} field={field} writable={writable}
                       onEdit={() => setEditing(field)}
                       onVerify={() => setVerifying(field)} />
          ))}
        </div>
      </Section>

      <Section testId="pl-public-preview"
               title="What the public sees"
               description="Only verified, publishable, non-sensitive facts ever reach a public page.
                            Contact details publish once recorded: saying where to write is not a
                            claim about who ConfirmPH is registered with.">
        <PublicPreview preview={identity.public_preview} meta={identity.meta} />
      </Section>

      {launch && (
        <Section testId="pl-launch-items"
                 title="Launch readiness"
                 description="An item backed by a record or by a live check reads its status from
                              that source and cannot be ticked here."
                 actions={<TonePill tone={launchTone(launch.state)}>
                   {LAUNCH_STATE_LABELS[launch.state] || launch.state}
                 </TonePill>}>
          <Table head={["Item", "Scope", "Status", "Detail"]} testId="pl-launch-table"
                 minWidth={620}>
            {launch.items.map((item) => (
              <tr key={item.key} data-testid={`pl-launch-item-${item.key}`}>
                <td className="py-2 pr-3">
                  <div className="font-medium text-slate-900">{item.title}</div>
                  <div className="text-xs text-slate-500">{item.requirement}</div>
                </td>
                <td className="py-2 pr-3 text-xs text-slate-600">{humanise(item.scope)}</td>
                <td className="py-2 pr-3">
                  <div className="flex items-center gap-1.5">
                    <StatusPill status={item.status} />
                    {item.derived && <Lock className="w-3 h-3 text-slate-400" title="Derived" />}
                  </div>
                </td>
                <td className="py-2 pr-3 text-xs text-slate-600">
                  {item.source_detail || item.notes || (item.waived ? item.waiver_reason : "—")}
                </td>
              </tr>
            ))}
          </Table>
        </Section>
      )}

      {editing && (
        <RecordModal field={editing} onClose={() => setEditing(null)}
                     onSaved={() => { setEditing(null); load(); }} />
      )}
      {verifying && (
        <VerifyModal field={verifying} onClose={() => setVerifying(null)}
                     onSaved={() => { setVerifying(null); load(); }} />
      )}
    </div>
  );
}

function FieldCard({ field, writable, onEdit, onVerify }) {
  return (
    <div className="rounded-xl border border-slate-200 p-3" data-testid={`pl-field-${field.key}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-900">{field.label}</div>
          <div className="text-xs text-slate-500 mt-0.5">{field.description}</div>
        </div>
        <StatusPill status={field.effective_status} />
      </div>
      <div className="mt-2 text-sm text-slate-800 break-words">
        {field.value || <span className="text-slate-400">Not recorded</span>}
        {field.value_masked && <span className="ml-1 text-[11px] text-slate-500">(masked)</span>}
      </div>
      <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
        {field.verified_by_name && (
          <><dt>Verified by</dt><dd className="text-right text-slate-700">{field.verified_by_name}</dd></>
        )}
        {field.verified_at && (
          <><dt>Verified</dt><dd className="text-right">{fmtDate(field.verified_at)}</dd></>
        )}
        {field.expires_at && (
          <><dt>Expires</dt><dd className="text-right">{fmtDate(field.expires_at)}</dd></>
        )}
        {field.source_reference && (
          <><dt>Source</dt><dd className="text-right truncate">{field.source_reference}</dd></>
        )}
      </dl>
      {field.external_action && field.effective_status !== "verified" && (
        <ExternalActionNote />
      )}
      {writable && (
        <div className="mt-2 flex flex-wrap gap-2">
          <Button variant="secondary" onClick={onEdit}
                  data-testid={`pl-edit-${field.key}`}>Record</Button>
          <Button variant="secondary" onClick={onVerify}
                  data-testid={`pl-verify-${field.key}`}>
            <ShieldCheck className="w-4 h-4" /> Verify
          </Button>
        </div>
      )}
    </div>
  );
}

function PublicPreview({ preview, meta }) {
  return (
    <div className="space-y-2 text-sm">
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
        <div className="font-medium text-slate-900">{preview.operator_name}</div>
        {preview.disclosure && <p className="mt-1 text-slate-700">{preview.disclosure}</p>}
        <dl className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
          <Row label="Legal entity recorded" value={preview.legal_entity_recorded ? "Yes" : "No"} />
          <Row label="Support contact" value={preview.support_email || "Not recorded"} />
          <Row label="Privacy contact" value={preview.privacy_contact || "Not recorded"} />
          <Row label="Registrations shown"
               value={preview.registrations?.length
                 ? preview.registrations.map((r) => r.authority).join(", ")
                 : "None"} />
          <Row label="Trustmark" value={preview.trustmark ? "Approved" : "Not shown"} />
          <Row label="Public disclosure"
               value={meta?.public_disclosure_enabled ? "Enabled" : "Off"} />
        </dl>
      </div>
      <Note tone="slate">
        Registration numbers are never published. What can be published is the fact that a
        registration exists, which is what a consumer needs in order to check it themselves.
      </Note>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <>
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-slate-800 sm:text-right break-words">{value}</dd>
    </>
  );
}

// Exported with an optional ``submit`` so the Legal Readiness Center can
// record its own tracked records through the same form and the same rules.
export function RecordModal({ field, onClose, onSaved, submit }) {
  const [form, setForm] = useState({
    status: field.status === "verified" ? "recorded_not_verified" : field.status,
    value: field.value_masked ? "" : (field.value || ""),
    source_reference: field.source_reference || "",
    expires_at: field.expires_at || "",
    review_due_at: field.review_due_at || "",
    notes: field.notes || "",
    reason: "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (field.value_masked && !payload.value) delete payload.value;
      await (submit ? submit(payload) : plPatch(`/identity/fields/${field.key}`, payload));
      toast.success("Recorded.");
      onSaved();
    } catch (err) {
      toast.error(legalError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Record: ${field.label}`} onClose={onClose} testId="pl-record-modal"
           description="Record what is currently known. Verification is a separate action.">
      <Field label="Status">
        <select className={inputClass} value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                data-testid="pl-record-status">
          {SELECTABLE.map((status) => (
            <option key={status} value={status}>{statusLabel(status)}</option>
          ))}
        </select>
      </Field>
      <Note tone="slate">
        "Verified" is not in this list on purpose. It is only reachable through Verify, which
        requires evidence and records who checked it.
      </Note>
      <Field label="Value"
             hint={field.value_masked ? "Leave blank to keep the stored value." : undefined}>
        <input className={inputClass} value={form.value}
               onChange={(e) => setForm({ ...form, value: e.target.value })}
               data-testid="pl-record-value" />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Expires"><input type="date" className={inputClass} value={form.expires_at}
               onChange={(e) => setForm({ ...form, expires_at: e.target.value })} /></Field>
        <Field label="Review due"><input type="date" className={inputClass} value={form.review_due_at}
               onChange={(e) => setForm({ ...form, review_due_at: e.target.value })} /></Field>
      </div>
      <Field label="Source reference">
        <input className={inputClass} value={form.source_reference}
               onChange={(e) => setForm({ ...form, source_reference: e.target.value })} />
      </Field>
      <Field label="Notes">
        <textarea className={inputClass} rows={3} value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} />
      </Field>
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={save} busy={busy} data-testid="pl-record-save">Save</Button>
      </div>
    </Modal>
  );
}

export function VerifyModal({ field, onClose, onSaved, submit }) {
  const [form, setForm] = useState({
    reason: "", source_reference: field.source_reference || "",
    expires_at: field.expires_at || "", review_due_at: field.review_due_at || "",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const verify = async () => {
    setBusy(true);
    try {
      let refs;
      if (file) {
        const uploaded = await uploadEvidence(file, "registration_certificate");
        refs = [{ type: "evidence", id: uploaded.id }];
      }
      const payload = { ...form, ...(refs ? { evidence_refs: refs } : {}) };
      await (submit ? submit(payload) : plPost(`/identity/fields/${field.key}/verify`, payload));
      toast.success(`${field.label} verified.`);
      onSaved();
    } catch (err) {
      toast.error(legalError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Verify: ${field.label}`} onClose={onClose} testId="pl-verify-modal"
           description="Verification needs the recorded value, something it was checked against,
                        and what you checked.">
      <Field label="What did you check, and against what?">
        <textarea className={inputClass} rows={3} value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  data-testid="pl-verify-reason"
                  placeholder="Checked the registration number against the certificate uploaded below." />
      </Field>
      <Field label="Attach the document"
             hint="Or record a source reference below if the evidence lives elsewhere.">
        <input type="file" className="text-sm" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic"
               onChange={(e) => setFile(e.target.files?.[0] || null)}
               data-testid="pl-verify-file" />
      </Field>
      <Field label="Source reference">
        <input className={inputClass} value={form.source_reference}
               onChange={(e) => setForm({ ...form, source_reference: e.target.value })} />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Expires"><input type="date" className={inputClass} value={form.expires_at}
               onChange={(e) => setForm({ ...form, expires_at: e.target.value })} /></Field>
        <Field label="Review due"><input type="date" className={inputClass} value={form.review_due_at}
               onChange={(e) => setForm({ ...form, review_due_at: e.target.value })} /></Field>
      </div>
      <Note tone="amber">
        An expiry date is honoured automatically: once it passes, this stops counting as verified
        everywhere, including on any public page.
      </Note>
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={verify} busy={busy} data-testid="pl-verify-save">Verify</Button>
      </div>
    </Modal>
  );
}

/** Read a file as base64 and upload it to the compliance evidence store. */
export async function uploadEvidence(file, category, subject = {}) {
  const data_base64 = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  return plPost("/evidence", {
    data_base64, content_type: file.type || "application/pdf",
    filename: file.name, category, ...subject,
  });
}
